import { PermissionGate } from "@components/PermissionGate";
import { useTranslation } from "react-i18next";
import { ActionIcon, Button, NumberInput } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { DailyTimeBlock } from "@customTypes/slotTemplate";

import { StyledTimeBlockManager } from "./TimeBlockManager.styled";


interface TimeBlockManagerProps {
  selectedDays: number[];
  dailySchedule: Record<number, DailyTimeBlock[]>;
  onChange: (dailySchedule: Record<number, DailyTimeBlock[]>) => void;
}

export const TimeBlockManager = ({
  selectedDays,
  dailySchedule,
  onChange,
}: TimeBlockManagerProps) => {
  const { t } = useTranslation();

  const dayNames = [
    t("sun-short"),
    t("mon-short"),
    t("tue-short"),
    t("wed-short"),
    t("thu-short"),
    t("fri-short"),
    t("sat-short"),
  ];

  const addTimeBlock = (day: number) => {
    const newSchedule = { ...dailySchedule };
    if (!newSchedule[day]) {
      newSchedule[day] = [];
    }
    newSchedule[day] = [
      ...newSchedule[day],
      {
        startTime: "09:00",
        endTime: "17:00",
        slotDurationMinutes: 30,
        bufferTimeMinutes: 5,
      },
    ];
    onChange(newSchedule);
  };

  const removeTimeBlock = (day: number, blockIndex: number) => {
    const newSchedule = { ...dailySchedule };
    newSchedule[day] = newSchedule[day].filter((_, idx) => idx !== blockIndex);
    if (newSchedule[day].length === 0) {
      delete newSchedule[day];
    }
    onChange(newSchedule);
  };

  const updateTimeBlock = (
    day: number,
    blockIndex: number,
    field: keyof DailyTimeBlock,
    value: string | number
  ) => {
    const newSchedule = { ...dailySchedule };
    if (!newSchedule[day]) return;

    newSchedule[day] = newSchedule[day].map((block, idx) => {
      if (idx === blockIndex) {
        return { ...block, [field]: value };
      }
      return block;
    });
    onChange(newSchedule);
  };

  if (selectedDays.length === 0) {
    return (
      <StyledTimeBlockManager>
        <div className="no-days-selected">
          {t("select-days-to-configure-time-blocks")}
        </div>
      </StyledTimeBlockManager>
    );
  }

  return (
    <StyledTimeBlockManager>
      {selectedDays.sort((a, b) => a - b).map(day => (
        <div key={day} className="day-section">
          <div className="day-header">
            <span className="day-name">{dayNames[day]}</span>
          </div>

          <div className="time-blocks">
            {(dailySchedule[day] || []).map((block, blockIndex) => (
              <div key={blockIndex} className="time-block">
                <TimeInput
                  className="time-field"
                  label={blockIndex === 0 ? t("start-time") : ""}
                  value={block.startTime}
                  onChange={(e) =>
                    updateTimeBlock(day, blockIndex, "startTime", e.currentTarget.value)
                  }
                />

                <TimeInput
                  className="time-field"
                  label={blockIndex === 0 ? t("end-time") : ""}
                  value={block.endTime}
                  onChange={(e) =>
                    updateTimeBlock(day, blockIndex, "endTime", e.currentTarget.value)
                  }
                />

                <NumberInput
                  className="time-field"
                  label={blockIndex === 0 ? t("slot-duration") : ""}
                  min={5}
                  max={240}
                  step={5}
                  value={block.slotDurationMinutes}
                  onChange={(val) =>
                    updateTimeBlock(day, blockIndex, "slotDurationMinutes", Number(val))
                  }
                />

                <NumberInput
                  className="time-field"
                  label={blockIndex === 0 ? t("buffer-mins") : ""}
                  min={0}
                  max={60}
                  step={5}
                  value={block.bufferTimeMinutes}
                  onChange={(val) =>
                    updateTimeBlock(day, blockIndex, "bufferTimeMinutes", Number(val))
                  }
                />

                <PermissionGate permission="appointments.slots"><ActionIcon
                  className="remove-btn"
                  variant="subtle"
                  color="red"
                  onClick={() => removeTimeBlock(day, blockIndex)}>
                  <IconTrash size={16} />
                </ActionIcon></PermissionGate>
              </div>
            ))}

            <PermissionGate permission="appointments.slots"><Button
              className="add-block-btn"
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => addTimeBlock(day)}>
              {t("add-time-block")}
            </Button></PermissionGate>
          </div>
        </div>
      ))}
    </StyledTimeBlockManager>
  );
};
